import React, { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const CustomersCRUD = React.memo(({ config, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [sectionContent, setSectionContent] = useState({
    title: 'Our Valued Customers',
    subtitle: 'Trusted by industry leaders',
    description: 'Join hundreds of mortgage professionals who trust our AI solutions'
  });
  
  const customers = config?.customers || {};
  const companies = customers.companies || [];
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '/api/placeholder/150/60'
  });

  // Memoized event handlers to prevent re-renders
  const handleNameChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, description: e.target.value }));
  }, []);

  const handleLogoChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, logo: e.target.value }));
  }, []);

  // Save section content (like Value Propositions pattern)
  const saveSectionContent = async () => {
    setLoading(true);
    setSaveStatus('');
    
    try {
      // Save each section content field
      for (const [field, value] of Object.entries(sectionContent)) {
        await axios.put(`${API_BASE_URL}/api/admin/config/customers/${field}`, {
          value: value,
          data_type: 'string'
        }, {
          headers: getAuthHeaders()
        });
      }

      // Reload config data first
      await onUpdate();
      
      // Then update UI state after fresh data is loaded
      setHasUnsavedChanges(false);
      setSaveStatus('success');
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving section content:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 5000);
    }
    setLoading(false);
  };

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo: '/api/placeholder/150/60'
    });
    setEditingIndex(-1);
    setShowAddForm(false);
  };

  const handleEdit = (index) => {
    setFormData({ ...companies[index] });
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let updatedCompanies = [...companies];
      
      if (editingIndex >= 0) {
        // Update existing
        updatedCompanies[editingIndex] = { ...formData };
      } else {
        // Add new
        updatedCompanies.push({ ...formData });
      }

      await axios.put(`${API_BASE_URL}/api/admin/config/customers/companies`, {
        value: updatedCompanies,
        data_type: 'json'
      }, {
        headers: getAuthHeaders()
      });

      await onUpdate();
      resetForm();
      alert('Company saved successfully!');
    } catch (error) {
      console.error('Error saving company:', error);
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error saving company');
      }
    }
    setLoading(false);
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    
    setLoading(true);
    try {
      const updatedCompanies = companies.filter((_, i) => i !== index);
      
      await axios.put(`${API_BASE_URL}/api/admin/config/customers/companies`, {
        value: updatedCompanies,
        data_type: 'json'
      }, {
        headers: getAuthHeaders()
      });

      await onUpdate();
      alert('Company deleted successfully!');
    } catch (error) {
      console.error('Error deleting company:', error);
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error deleting company');
      }
    }
    setLoading(false);
  };

  const updateTextField = async (field, value) => {
    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/api/admin/config/customers/${field}`, {
        value: value,
        data_type: 'string'
      }, {
        headers: getAuthHeaders()
      });
      await onUpdate();
    } catch (error) {
      console.error('Error updating field:', error);
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error updating field');
      }
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-blue-900">Customer Section</h4>
        <p className="text-blue-700 mt-2">
          Manage the customer showcase section including title, subtitle, and featured companies.
        </p>
      </div>

      {/* Section Title and Subtitle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h5 className="font-semibold text-gray-900 mb-4">Section Content</h5>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={customers.title || 'Trusted by Leading Mortgage Professionals'}
              onChange={(e) => updateTextField('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Subtitle
            </label>
            <textarea
              value={customers.subtitle || 'Join thousands of mortgage professionals who trust our AI agents to streamline their workflow and improve customer outcomes.'}
              onChange={(e) => updateTextField('subtitle', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              disabled={loading}
            />
          </div>
        </div>
        
        {/* Save/Discard buttons */}
        {hasUnsavedChanges && (
          <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3"></div>
              <span className="text-sm text-yellow-800">You have unsaved changes</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setHasUnsavedChanges(false)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                disabled={loading}
              >
                Discard
              </button>
              <button
                onClick={saveSectionContent}
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Content</span>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Save status message */}
        {saveStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            Content saved successfully!
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            Error saving content. Please try again.
          </div>
        )}
        
        {/* Save/Discard buttons */}
        {hasUnsavedChanges && (
          <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3"></div>
              <span className="text-sm text-yellow-800">You have unsaved changes</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setHasUnsavedChanges(false)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                disabled={loading}
              >
                Discard
              </button>
              <button
                onClick={saveSectionContent}
                className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Content</span>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Save status message */}
        {saveStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            Content saved successfully!
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            Error saving content. Please try again.
          </div>
        )}
      </div>

      {/* Companies Management */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h5 className="font-semibold text-gray-900">Featured Companies</h5>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            disabled={loading}
          >
            Add Company
          </button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 mb-6">
            <h6 className="font-medium text-gray-900 mb-4">
              {editingIndex >= 0 ? 'Edit Company' : 'Add New Company'}
            </h6>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  type="text"
                  value={formData.logo}
                  onChange={handleLogoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={handleDescriptionChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Company'}
              </button>
            </div>
          </form>
        )}

        {/* Companies List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((company, index) => (
            <div key={`customer-${company.name}-${index}`} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <img
                  src={company.logo || '/api/placeholder/150/60'}
                  alt={company.name}
                  className="h-12 object-contain"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <h6 className="font-medium text-gray-900">{company.name}</h6>
              {company.description && (
                <p className="text-sm text-gray-600 mt-1">{company.description}</p>
              )}
            </div>
          ))}
        </div>

        {companies.length === 0 && (
          <p className="text-gray-500 text-center py-8">
            No companies added yet. Click "Add Company" to get started.
          </p>
        )}
      </div>
    </div>
  );
});

export default CustomersCRUD;